// src/pages/FriendCirclesWelcome.jsx
import { motion } from 'framer-motion';
import { Users, Sparkles, MessageCircle, Zap, ArrowRight, BookOpen, HelpCircle, DoorOpen } from 'lucide-react';
import { usePrivacyAuth } from '../context/PrivacyAuthContext';
import { useApp } from '../context/AppContext';
import { useCirclesStore } from '../stores/circlesStore';
import { useEffect } from 'react';

export default function FriendCirclesWelcome() {
  const { user } = usePrivacyAuth();
  const { setCurrentView } = useApp();
  const { myCircles, loadMyCircles } = useCirclesStore();

  useEffect(() => {
    if (user?.userId) {
      loadMyCircles(user.userId);
    }
  }, [user?.userId, loadMyCircles]);

  const hasCircles = myCircles && myCircles.length > 0;

  const handleCreateCircle = () => {
    window.location.href = '/?view=friend-circles-setup&mode=create';
  };

  const handleJoinCircle = () => {
    window.location.href = '/?view=friend-circles-setup&mode=join';
  };

  const handleGoToCircles = () => {
    setCurrentView('circle-dashboard');
  };

  const benefits = [
    {
      icon: BookOpen,
      title: 'The same module',
      description: 'The circle picks a module. You can see whether the others have finished it, and nothing else.',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      icon: HelpCircle,
      title: 'One question, all at once',
      description: 'Everyone picks from a few fixed answers. Nobody sees anyone\u2019s until everyone has, then they show together, with names.',
      gradient: 'from-orange-500 to-yellow-500',
    },
    {
      icon: MessageCircle,
      title: 'Talk about it in person',
      description: 'A few questions to take with you. There\u2019s nowhere in a circle to type anything.',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: DoorOpen,
      title: 'Nobody in charge',
      description: 'Nobody can remove anyone. Anyone can leave at any time, and nobody is told.',
      gradient: 'from-red-500 to-pink-500',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-20 sm:pt-32">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-6xl">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <motion.div
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto bg-gradient-to-br from-purple-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-2xl">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </div>
          </motion.div>

          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-4 sm:mb-6">
            Welcome to{' '}
            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 text-transparent bg-clip-text">
              Friend Circles
            </span>
            ! 🎉
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-purple-200 mb-6 sm:mb-8 max-w-3xl mx-auto">
            Hi {user?.username || 'there'}. Here&apos;s how a circle works.
          </p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-2xl p-6 sm:p-8 max-w-3xl mx-auto mb-8 sm:mb-12"
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-yellow-400 flex-shrink-0 mt-1" />
              <div className="text-left">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 sm:mb-3">
                  The modules, with your friends
                </h3>
                <p className="text-base sm:text-lg text-purple-100 leading-relaxed">
                  A Friend Circle is you and friends you already know, on the same module. You answer the same
                  question, see everyone&apos;s answers at the same moment, and talk about it when you&apos;re
                  together. There are no posts, comments or chat, so there&apos;s nothing to write and nothing to
                  pile on.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Benefits Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 sm:p-8 hover:bg-white/10 transition-all duration-300"
            >
              <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${benefit.gradient} rounded-xl flex items-center justify-center mb-4`}>
                <benefit.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                {benefit.title}
              </h3>
              <p className="text-sm sm:text-base text-purple-200 leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-400/20 rounded-3xl p-6 sm:p-10 mb-12 sm:mb-16"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8 text-center">
            How It Works
          </h2>

          <div className="space-y-6 sm:space-y-8 max-w-3xl mx-auto">
            <div className="flex gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center font-bold text-white text-lg sm:text-xl">
                  1
                </div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Create or Join a Circle</h3>
                <p className="text-sm sm:text-base text-purple-200">
                  Start a new circle with a custom name and emoji, or join an existing one with a friend's invite code.
                </p>
              </div>
            </div>

            <div className="flex gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center font-bold text-white text-lg sm:text-xl">
                  2
                </div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Invite Your Friends</h3>
                <p className="text-sm sm:text-base text-purple-200">
                  Send the code privately to friends you know, never anywhere public. Anyone with it can join, and anyone in the circle can swap it for a new one.
                </p>
              </div>
            </div>

            <div className="flex gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-full flex items-center justify-center font-bold text-white text-lg sm:text-xl">
                  3
                </div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Read, answer, reveal</h3>
                <p className="text-sm sm:text-base text-purple-200">
                  Do the module at your own pace. Answer the circle&apos;s question when you&apos;re ready; it stays
                  hidden until everyone has, or until three days have passed with at least two answers in.
                </p>
              </div>
            </div>

            <div className="flex gap-4 sm:gap-6">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center font-bold text-white text-lg sm:text-xl">
                  4
                </div>
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">Then talk</h3>
                <p className="text-sm sm:text-base text-purple-200">
                  The answers are where the conversation starts. Have it on the way home, not in the app.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center"
        >
          {hasCircles ? (
            // If user has circles, show "Go to My Circles" button prominently
            <button
              onClick={handleGoToCircles}
              className="group w-full sm:w-auto bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-2xl font-bold text-lg sm:text-xl shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
            >
              <Users className="w-6 h-6" />
              Go to My Circles
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          ) : (
            // If user has no circles, show create/join buttons
            <>
              <button
                onClick={handleCreateCircle}
                className="group w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-2xl font-bold text-lg sm:text-xl shadow-2xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <Users className="w-6 h-6" />
                Create Your Circle
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={handleJoinCircle}
                className="group w-full sm:w-auto bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/20 text-white px-8 sm:px-12 py-4 sm:py-5 rounded-2xl font-bold text-lg sm:text-xl transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
              >
                <Zap className="w-6 h-6" />
                Join a Circle
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </>
          )}
        </motion.div>

        {/* Closing line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="inline-block bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/30 rounded-2xl px-6 sm:px-8 py-4 sm:py-5">
            <p className="text-sm sm:text-base text-yellow-100 flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
              <Sparkles className="w-5 h-5 text-yellow-400 flex-shrink-0" />
              <span>Friend Circles are for people aged 13 and over.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
